import { Button, ChipLabel, Typography } from 'omakase';
import { LoadGoogleMaps } from 'components/maps/LoadGoogleMaps';
import { consumer } from '@tock/coretypes';
import { getGoogleMapsState } from 'selectors/appSelector';
import { pluralizeWord } from '@tock/shared/utils/pluralize';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import ProfileMap from 'components/profile/culinary-travel-log/ProfileMap';
import ProfileSection from 'components/profile/ProfileSection';
import Subheader from 'components/profile/Subheader';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';
import locationGroupedPurchaseHistorySelector, {
  LocationPurchaseHistory,
  locationPurchaseStatsSelector,
} from 'selectors/locationGroupedPurchaseHistorySelector';

interface OwnProps {
  isLoading: boolean;
  patronCity?: string;
  patronZip?: string;
}

interface ConnectProps extends DefaultConnectProps {
  locationPurchaseHistory: LocationPurchaseHistory[];
  travelLogStats: consumer.ProfileStats;
}

type Props = OwnProps & ConnectProps;

const CulinaryTravelLogSection = ({
  isLoading,
  locationPurchaseHistory,
  travelLogStats,
  patronCity,
  patronZip,
}: Props) => {
  const { googleMapsLoaded, googleMapsLoadError } = useSelector(getGoogleMapsState);
  return (
    <ProfileSection name="Your culinary travel log" isLoading={isLoading}>
      <div className="TravelLog-sectionLayoutContainer">
        <div className="TravelLog-mapSubsection">
          <LoadGoogleMaps>
            <ProfileMap
              locationPurchaseHistory={locationPurchaseHistory}
              patronZip={patronZip}
              patronCity={patronCity}
              isMapLoaded={googleMapsLoaded}
              loadError={googleMapsLoadError}
            />
          </LoadGoogleMaps>
        </div>
        <div className="TravelLog-statsSubsection">
          {travelLogStats.reservations === 0 ? (
            <EmptyStatsBox />
          ) : (
            <StatsBox stats={travelLogStats} />
          )}
        </div>
      </div>
    </ProfileSection>
  );
};

const StatsBox = ({
  stats: { reservations, cities, countries },
}: {
  stats: consumer.ProfileStats;
}) => {
  return (
    <>
      <Subheader text="Your stats" />
      <StatsBoxRow value={reservations} label={pluralizeWord(reservations, 'Reservation')} />
      <StatsBoxRow value={cities} label={pluralizeWord(cities, 'City')} />
      <StatsBoxRow value={countries} label={pluralizeWord(countries, 'Country')} />
    </>
  );
};

const EmptyStatsBox = () => {
  const history = useHistory();
  const routeToHomePage = () => {
    history.push('/');
  };
  return (
    <>
      <Subheader text="No reservations" />
      <Typography component="p" variant="body02" mb={3}>
        You haven’t made any reservations yet. Book something soon, and watch your travel log grow.
      </Typography>
      <Button variant="primary" size="medium" onClick={routeToHomePage} fullWidth>
        Find a reservation
      </Button>
    </>
  );
};

const StatsBoxRow = ({ value, label }: { value: number; label: string }) => {
  return (
    <div className="TravelLog-statsBox_row">
      <ChipLabel size="medium" variant="subtle" highContrast>
        {value}
      </ChipLabel>
      <Typography component="span" variant="body02">
        {label}
      </Typography>
    </div>
  );
};

function mapStateToProps(state: StoreType) {
  return {
    locationPurchaseHistory: locationGroupedPurchaseHistorySelector(state),
    travelLogStats: locationPurchaseStatsSelector(state),
  };
}

export default connect<OwnProps, ConnectProps>(CulinaryTravelLogSection, mapStateToProps);
